import { useState, useEffect, useContext } from "react"
import { GlobalContextProvider } from "./functions/GlobalContext"
import {
    Box,
    Text,
    Center,
    HStack,
    Select,
    Button,
    Input,
    InputGroup,
    InputRightElement,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Spinner,
    Icon,
} from "@chakra-ui/react"
import { AiOutlineCheckCircle, AiOutlineCloseCircle, AiOutlineSearch } from 'react-icons/ai'
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'
import InfoModal from "./modals/InfoModal"
import Const from "../Consts"
import { Twirp, TwirpProps } from "./functions/Twirp"
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type PesilatRow = {
    id: number
    nama: string
    username: string
    country_id: number
    tahun_masuk: number
    athlete: boolean
    coach_level: number
    referee_level: number
}

const PER_PAGE_OPTIONS = [10, 50, 100]

const LEVEL_LIST = [
    { value: 0, label: "-" },
    { value: 1, label: "Ranting" },
    { value: 2, label: "City 1" }, { value: 3, label: "City 2" }, { value: 4, label: "City 3" },
    { value: 5, label: "Province 1" }, { value: 6, label: "Province 2" }, { value: 7, label: "Province 3" },
    { value: 8, label: "National 1" }, { value: 9, label: "National 2" }, { value: 10, label: "National 3" },
    { value: 11, label: "International 1" }, { value: 12, label: "International 2" }, { value: 13, label: "International 3" },
]

export default function PesilatList() {
    const globalContext = useContext(GlobalContextProvider)
    const { t } = useTranslation();
    const navigate = useNavigate()

    const [rows, setRows] = useState<PesilatRow[]>([])
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(10)
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [countryMap, setCountryMap] = useState<Record<number, string>>({})
    const [searchQuery, setSearchQuery] = useState("")
    const [activeSearch, setActiveSearch] = useState("")
    const [isSearchMode, setIsSearchMode] = useState(false)

    const [modalData, setModalData] = useState({
        title: "",
        main: "",
        isOpen: false,
    })
    const closeModal = () => {
        setModalData({ ...modalData, isOpen: false })
    }

    const totalPages = Math.max(1, Math.ceil(total / perPage))

    const findLevelLabel = (value: number) => {
        const found = LEVEL_LIST.find(item => item.value === value)
        return found ? found.label : "-"
    }

    const loadCountries = async () => {
        const props: TwirpProps = {
            url: Const.API_ListCountries, headers: {}, timeout: 5000, data: {}
        }
        const result = await Twirp(props)
        if (result.state == 0) {
            const commons = result.data.Commons as { id: number, nama: string }[]
            const map: Record<number, string> = {}
            commons.forEach(c => { map[c.id] = c.nama })
            setCountryMap(map)
        }
    }

    const loadData = async (pPage: number, pPerPage: number) => {
        setLoading(true)
        const props: TwirpProps = {
            url: Const.API_ListPesilatPaginated,
            headers: { Authorization: globalContext?.GetLoginData().token },
            timeout: 10000,
            data: { page: pPage, per_page: pPerPage }
        }
        const result = await Twirp(props)
        if (result.state == 2) {//other error
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: result.axiosError.message })
        } else if (result.state == 1) {//twirp controlled error
            let msg = result.axiosError.response?.data?.msg || result.axiosError.message
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: msg })
            if (result.axiosError.response?.status == 401) {
                navigate('/logout')
            }
        } else { //successful
            setRows(result.data.Pesilats || [])
            setTotal(result.data.total || 0)
        }
        setLoading(false)
    }

    const doSearch = async (query: string) => {
        if (query.trim() === "") {
            return
        }
        setLoading(true)
        const props: TwirpProps = {
            url: Const.API_SearchPesilat,
            headers: { Authorization: globalContext?.GetLoginData().token },
            timeout: 10000,
            data: { nama: query }
        }
        const result = await Twirp(props)
        if (result.state == 2) {//other error
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: result.axiosError.message })
        } else if (result.state == 1) {//twirp controlled error
            let msg = result.axiosError.response?.data?.msg || result.axiosError.message
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: msg })
            if (result.axiosError.response?.status == 401) {
                navigate('/logout')
            }
        } else { //successful
            setRows(result.data.Pesilats || [])
            setTotal(result.data.Pesilats ? result.data.Pesilats.length : 0)
        }
        setLoading(false)
    }

    useEffect(() => {
        loadCountries()
    }, [])

    useEffect(() => {
        if (!isSearchMode) {
            loadData(page, perPage)
        }
    }, [page, perPage, isSearchMode])

    const onSearch = () => {
        if (searchQuery.trim() === "") {
            return
        }
        setActiveSearch(searchQuery)
        setIsSearchMode(true)
        doSearch(searchQuery)
    }

    const onClearSearch = () => {
        setSearchQuery("")
        setActiveSearch("")
        setIsSearchMode(false)
        setPage(1)
    }

    const onPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setPerPage(+event.target.value)
        setPage(1)
    }

    const onPrevPage = () => {
        if (page > 1) {
            setPage(page - 1)
        }
    }

    const onNextPage = () => {
        if (page < totalPages) {
            setPage(page + 1)
        }
    }

    const onNameClick = (pesilatId: number) => {
        navigate("/profileview/" + pesilatId)
    }

    return (
        <Box py={"10px"} px={"10px"} borderWidth='0px' borderRadius='lg' overflow='hidden'>
            <InfoModal isOpen={modalData.isOpen} title={modalData.title} mainText={modalData.main} closeFunction={closeModal} />

            <HStack spacing={2} mb={"10px"}>
                <InputGroup size='sm' maxW={'300px'}>
                    <Input
                        type='text'
                        placeholder={t('search_placeholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') onSearch() }}
                    />
                    <InputRightElement width='2.5rem'>
                        <Button size='sm' h='1.75rem' p={0} onClick={onSearch} isDisabled={loading || searchQuery.trim() === ''}>
                            <Icon as={AiOutlineSearch} />
                        </Button>
                    </InputRightElement>
                </InputGroup>
                <Button size='sm' variant='outline' onClick={onSearch} isDisabled={loading || searchQuery.trim() === ''}>
                    {t('Search')}
                </Button>
                {isSearchMode ? (
                    <Button size='sm' variant='ghost' onClick={onClearSearch} isDisabled={loading}>
                        {t('show_all')}
                    </Button>
                ) : ''}
            </HStack>

            {isSearchMode && activeSearch !== "" ? (
                <Text fontSize='sm' mb={"5px"} color={'gray.500'}>
                    {t('search_results_for')} "{activeSearch}" ({total} {t('total_records')})
                </Text>
            ) : ''}

            {!isSearchMode ? (
                <HStack spacing={4} mb={"10px"} justify="space-between">
                    <HStack spacing={2}>
                        <Text fontSize='sm'>{t('per_page')}</Text>
                        <Select size='sm' w='80px' value={perPage} onChange={onPerPageChange}>
                            {PER_PAGE_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </Select>
                    </HStack>
                    <Text fontSize='sm'>{t('total_records')}: {total}</Text>
                </HStack>
            ) : ''}

            {loading ? (
                <Center py={"20px"}>
                    <Spinner size='lg' />
                </Center>
            ) : (
                <TableContainer>
                    <Table variant='simple' colorScheme='blue' size='sm'>
                        <Thead>
                            <Tr>
                                <Th>#</Th>
                                <Th>{t('Full Name')}</Th>
                                <Th>{t('Username')}</Th>
                                <Th>{t('Country')}</Th>
                                <Th>{t('joined_kpsn')}</Th>
                                <Th>{t('Athlete')}</Th>
                                <Th>{t('Coach Level')}</Th>
                                <Th>{t('Referee Level')}</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {rows.length === 0 ? (
                                <Tr>
                                    <Td colSpan={8}>
                                        <Center>{t('no_records')}</Center>
                                    </Td>
                                </Tr>
                            ) : (
                                rows.map((row, index) => (
                                    <Tr key={row.id}>
                                        <Td>{isSearchMode ? index + 1 : (page - 1) * perPage + index + 1}</Td>
                                        <Td>
                                            <Button variant='link' colorScheme='blue' size='sm' onClick={() => onNameClick(row.id)}>
                                                {row.nama}
                                            </Button>
                                        </Td>
                                        <Td>{row.username}</Td>
                                        <Td>{countryMap[row.country_id] || row.country_id}</Td>
                                        <Td>{row.tahun_masuk}</Td>
                                        <Td>
                                            {row.athlete ? (
                                                <Icon as={AiOutlineCheckCircle} color='green.400' w={5} h={5} />
                                            ) : (
                                                <Icon as={AiOutlineCloseCircle} color='gray.400' w={5} h={5} />
                                            )}
                                        </Td>
                                        <Td>{row.coach_level > 0 ? t(findLevelLabel(row.coach_level)) : '-'}</Td>
                                        <Td>{row.referee_level > 0 ? t(findLevelLabel(row.referee_level)) : '-'}</Td>
                                    </Tr>
                                ))
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>
            )}

            {!isSearchMode ? (
                <HStack spacing={4} mt={"10px"} justify="center">
                    <Button size='sm' leftIcon={<ChevronLeftIcon />} onClick={onPrevPage} isDisabled={page <= 1 || loading}>
                        {t('Prev')}
                    </Button>
                    <Text fontSize='sm'>{t('page')} {page} / {totalPages}</Text>
                    <Button size='sm' rightIcon={<ChevronRightIcon />} onClick={onNextPage} isDisabled={page >= totalPages || loading}>
                        {t('Next')}
                    </Button>
                </HStack>
            ) : ''}
        </Box>
    )
}
